import React from "react";
import { RadioGroup } from "@headlessui/react";

import { CheckCircleIcon } from "../../icons";

export type RadioCardOption<T> = {
  label: string;
  value: T;
  enabled: boolean;
  icon?: React.ReactNode;
};

type RadioCardProps<T> = {
  size?: "sm" | "md";
  label?: string;
  options: RadioCardOption<T>[];
  subtext?: string;
  selectedOption?: RadioCardOption<T>;
  setSelectedOption: React.Dispatch<any>;
  disabled?: boolean;
};

const RadioCards = <T extends string | number>({
  size = "sm",
  label,
  options,
  subtext,
  selectedOption,
  setSelectedOption,
  disabled,
}: RadioCardProps<T>) => {
  return (
    <div>
      <RadioGroup value={selectedOption} onChange={setSelectedOption}>
        {label && <RadioGroup.Label className="text-sm font-medium text-gray-700">{label}</RadioGroup.Label>}
        <div
          className={`mt-4 grid ${
            size === "md" ? "grid-cols-1 gap-y-6 sm:grid-cols-3 sm:gap-x-4" : "grid-cols-3 gap-3 sm:grid-cols-5"
          }`}
        >
          {options.map((option) => {
            return (
              <RadioGroup.Option
                key={option.label}
                value={option}
                className={({ active, checked }) => {
                  return `
                  ${
                    option.enabled && !disabled
                      ? "cursor-pointer focus:outline-none "
                      : "opacity-25 cursor-not-allowed "
                  }
                  ${active ? "ring-2 ring-offset-2 ring-primary-500 " : " "}
                  ${disabled ? "" : "hover:bg-gray-50 "}
                  ${
                    size === "sm"
                      ? checked
                        ? "bg-primary-600 border-transparent text-white hover:bg-primary-700 "
                        : "bg-white border-gray-200 text-gray-900 "
                      : ""
                  }
                  ${size === "md" ? (checked ? "hover:bg-white" : "bg-white border-gray-200 text-gray-900 ") : ""}
                  border rounded-md ${
                    size === "md" ? "p-4" : "p-3"
                  } relative flex justify-center shadow-sm text-sm sm:flex-1`;
                }}
                disabled={disabled ? disabled : !option.enabled}
              >
                {({ active, checked }) => {
                  return (
                    <>
                      {option.icon ? (
                        <>
                          <span className="flex flex-1">
                            <span className="flex flex-col">
                              {option.icon}
                              <RadioGroup.Description
                                as="span"
                                className="mt-1 flex items-center text-sm text-gray-500"
                              >
                                {option.label}
                              </RadioGroup.Description>
                            </span>
                          </span>
                          <CheckCircleIcon
                            className={`${!checked ? "invisible" : ""} h-5 w-5 shrink-0 text-emerald-600`}
                            aria-hidden="true"
                          />
                          <span
                            className={`${active ? "border " : "border-2 "}
                  ${checked ? "border-emerald-600 " : "border-transparent "}
                  pointer-events-none absolute -inset-px rounded-lg`}
                            aria-hidden="true"
                          />
                        </>
                      ) : (
                        <RadioGroup.Description as="span">{option.label}</RadioGroup.Description>
                      )}
                    </>
                  );
                }}
              </RadioGroup.Option>
            );
          })}
        </div>
      </RadioGroup>
      {subtext && (
        <p className="mt-2 text-xs text-gray-400" id="description">
          {subtext}
        </p>
      )}
    </div>
  );
};

export default RadioCards;
